import { Dialog, DialogTitle, List, ListItem, ListItemButton, ListItemText, useTheme } from '@mui/material';
import { useEnvs } from '@next-box/envs';
import { useFeature } from '@next-box/feature-flags';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { capitalize } from 'lodash-es';
import { useState } from 'react';
import { FeaturePlayerMovieModal } from '#components/FeaturePlayerModal/Movie/index.tsx';
import { FeaturePlayerTvModal } from '#components/FeaturePlayerModal/Tv/index.tsx';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { enable as enableFullscreenLock } from '#helpers/fullscreenLock.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { type ActiveVideo, type PlayerTypeLowerCase } from '#types.ts';
import { FeaturePlayer } from '../FeaturePlayer/index.tsx';
import { TrailerPlayer } from '../TrailerPlayer/index.tsx';
import { VideoModal } from './VideoModal.tsx';

export type PlayerSelectorModalProps = {
  activeVideo: ActiveVideo;
  onClose: () => void;
  type: ScreenType;
};

export const PlayerSelectorModal = ({ activeVideo, onClose, type }: PlayerSelectorModalProps) => {
  const [player, setPlayer] = useState<PlayerTypeLowerCase | undefined>();
  const theme = useTheme();
  const feature = useFeature(FeatureFlags.ExoticPinkPtarmigan);
  const { uaParser } = useToolbelt();
  const { getEnv } = useEnvs();
  const pathIds = activeVideo.pathIds ?? [activeVideo.id];
  const baseUrl = getEnv('NEXT_PUBLIC_EXOTIC_PINK_PTARMIGAN_PROVIDER') ?? '';

  const players = {
    feature: <FeaturePlayer url={`${atob(baseUrl)}/${type.toLowerCase()}/${pathIds.join('/')}`} />,
    trailer: <TrailerPlayer url={activeVideo.src} />,
  };

  return (
    <Dialog
      aria-labelledby={`${String(activeVideo.id)}-player-selector-title`}
      id={`${String(activeVideo.id)}-player-selector`}
      onClose={() => {
        setPlayer(undefined);
        onClose();
      }}
      open
      sx={{
        '.MuiDialog-paper': {
          backgroundColor: theme.palette.grey[900],
          width: '50%',
          [theme.breakpoints.down('md')]: {
            margin: theme.spacing(2),
          },
        },
      }}
    >
      <DialogTitle id={`${String(activeVideo.id)}-player-selector-title`} sx={{ color: theme.palette.grey[100] }}>
        Watch:
      </DialogTitle>
      {player === 'feature' && type === ScreenType.Tv ? (
        <FeaturePlayerTvModal
          id={activeVideo.id}
          initialPathIds={pathIds}
          onClose={onClose}
          open
          title={activeVideo.title}
          type={type}
        />
      ) : player === 'feature' && type === ScreenType.Movie ? (
        <FeaturePlayerMovieModal id={activeVideo.id} onClose={onClose} open title={activeVideo.title} type={type} />
      ) : player ? (
        <VideoModal id={activeVideo.id} onClose={onClose} open title={activeVideo.title}>
          {players[player]}
        </VideoModal>
      ) : undefined}
      <List>
        {activeVideo.src.length > 0 ? (
          <ListItem disableGutters key="trailer">
            <ListItemButton
              onClick={() => {
                setPlayer('trailer');
                void enableFullscreenLock(uaParser);
              }}
              sx={{ color: theme.palette.grey[400] }}
            >
              <ListItemText primary="Trailer" />
            </ListItemButton>
          </ListItem>
        ) : undefined}
        {feature ? (
          <ListItem disableGutters key="feature">
            <ListItemButton
              onClick={() => {
                setPlayer('feature');
                void enableFullscreenLock(uaParser);
              }}
              sx={{ color: theme.palette.grey[400] }}
            >
              <ListItemText primary={capitalize(type.toLowerCase())} />
            </ListItemButton>
          </ListItem>
        ) : undefined}
      </List>
    </Dialog>
  );
};
